export type Ability = {
  /**
   * String defining the type of subject this ability applies to.
   *
   * Generally not evaluated during authorization and mainly serves as a debug
   * tool, except for one specific case.
   *
   * If `subject` is `all`, no further checks are made and the current user is
   * allowed to perform actions in this ability on any resource they want.
   */
  subject: string | object

  /**
   * List of actions covered by this ability. The current resource can perform
   * all actions listed on
   *
   * - any resource, if `subject` is `all`
   * - any resource which satisfies given conditions, if they are given
   */
  actions: string[]

  /**
   * Optionally adds conditions which a resource must satisfy in order to
   * perform any of the listed actions.
   *
   * If a resource is a membership, which has the shape of
   *
   * ```
   * { id, user_id, team_id, role }
   * ```
   *
   * Then the condition could be something like
   *
   * ```
   * { role: 'annotator' }
   * ```
   *
   * That means the current user is authorized to perform actions listed in this
   * ability to members of role `annotator` and only those members.
   *
   * If a different shape of resource is given, or it has a different rule, the
   * current user is not allowed to perform any actions in this ability.
   *
   * The condition could also be an array, such as
   *
   * ```
   * { role: ['annotator', 'member'] }
   * ```
   *
   * In that case, the current user is authorized to perform actions listed in
   * this ability to members of roles either `annotator` or `member`.
   */
  conditions?: { [s: string]: number | string | string[] | boolean }
}

export type AbilityOptions = {
  subject: string | undefined
  // Anything can be passed in here and anything can be compared with anything,
  // including nested arrays and nested objects. We should aim to make this a
  // simple record instead
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  resource: any | null
}

export enum LoadingStatus {
  Unloaded = 'unloaded',
  Loading = 'loading',
  Loaded = 'loaded',
}

export type ValidationErrors = {
  [s: string]: string | [string] | ValidationErrors | ValidationErrors[]
}

export * from '@/store/types/AnnotationActorPayload'
export * from '@/store/types/AnnotationClassPayload'
export * from '@/store/types/AnnotationHotkeysPayload'
export * from '@/store/types/AnnotationTypePayload'
export * from '@/store/types/ClassUsagePayload'
export * from '@/store/types/ClientTeamInvitationPayload'
export * from '@/store/types/Dataset'
export * from '@/store/types/DatasetDetailPayload'
export * from '@/store/types/DatasetExportPayload'
export * from '@/store/types/DatasetFolderPayload'
export * from '@/store/types/DatasetItemCountsPayload'
export * from '@/store/types/DatasetItemFilenamePayload'
export * from '@/store/types/DatasetItemFilter'
export * from '@/store/types/DatasetItemStatus'
export * from '@/store/types/DatasetItemType'
export * from '@/store/types/DatasetPayload'
export * from '@/store/types/DatasetReportPayload'
export * from '@/store/types/DatasetUploadedItemsPayload'
export * from '@/store/types/DatasetUploadItemPayload'
export * from '@/store/types/FeaturePayload'
export * from '@/store/types/ImagePayload'
export * from '@/store/types/InputTag'
export * from '@/store/types/InstanceCountReportPayload'
export * from '@/store/types/InvitationPayload'
export * from '@/store/types/Login2FAResponsePayload'
export * from '@/store/types/LoginResponsePayload'
export * from '@/store/types/LogoutResponsePayload'
export * from '@/store/types/MembershipPayload'
export * from '@/store/types/MembershipRole'
export * from '@/store/types/NotificationMessagePayload'
export * from '@/store/types/NotificationPayload'
export * from '@/store/types/PaginationTypes'
export * from '@/store/types/ReviewStatus'
export * from '@/store/types/Setup2FAResponsePayload'
export * from '@/store/types/SkippedReason'
export * from '@/store/types/StageActionType'
export * from '@/store/types/StageAnnotationPayload'
export * from '@/store/types/StageType'
export * from '@/store/types/StoragePayload'
export * from '@/store/types/StorageProvider'
export * from '@/store/types/TeamPayload'
export * from '@/store/types/TeamUploadInfoPayload'
export * from '@/store/types/UserPayload'
export * from '@/store/types/V2CommentPayload'
export * from '@/store/types/V2CommentThreadPayload'
export * from '@/store/types/V2DatasetFolderPayload'
export * from '@/store/types/V2DatasetItemFilter'
export * from '@/store/types/V2DatasetItemPayload'
export * from '@/store/types/V2DatasetItemTimeSummaryPayload'
export * from '@/store/types/V2DatasetItemSlot'
export * from '@/store/types/V2InstanceStatus'
export * from '@/store/types/V2WorkflowEdgePayload'
export * from '@/store/types/V2WorkflowItemPayload'
export * from '@/store/types/V2WorkflowItemStatePayload'
export * from '@/store/types/V2WorkflowPayload'
export * from '@/store/types/V2WorkflowStageConfigPayload'
export * from '@/store/types/V2WorkflowStageInstancePayload'
export * from '@/store/types/V2WorkflowStagePayload'
export * from '@/store/types/ValidationError'
export * from '@/store/types/VideoFramePayload'
export * from '@/store/types/WorkforceManagerPayload'
export * from '@/store/types/RenderableImage'
export * from '@/store/types/LoadedImage'
export * from '@/store/types/LoadedFrame'
export * from '@/store/types/LoadedVideo'
export * from '@/store/types/CalloutPayload'
export * from '@/store/types/InstructionPayload'
export * from '@/store/types/InstructionVersionPayload'
export * from '@/store/types/InstructionVersionIdPayload'

export type ApiResponse<T> = { data: T }
